import {
  Box,
  FlexBox,
  Icon,
  IconButton,
  IconNames,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';
import cx from 'classnames';
import { useSettingsQuery } from 'lib-knowledge-management-common/graphql';
import { useModal } from 'lib-knowledge-modal';
import { routeGenerator } from 'lib-knowledge-utils/routes';
import { useRef } from 'react';
import { NavLink } from 'react-router-dom';

import { NodeLockIconWithTooltip } from 'components/icons/nodeLockIcon/nodeLockIcon';
import { useInfiniteScroll } from 'hooks/useInfiniteScroll';
import { useShowArticleSuggestions } from 'hooks/useShowArticleSuggestions';
import { useSidebarCollapsed } from 'hooks/useSidebarCollapsed';

import layoutStyles from '../layout/layout.module.scss';
import { CollectionAddModal } from '../modals/collectionAddModal';

import sidebarStyles from './sidebar.module.scss';

type CollectionLink = {
  title: string;
  id: string;
  internalKbEnabled: boolean;
  internalOnly: boolean;
  isSyncedWithElevio: boolean;
  onClick: (id: string) => void;
};
type SidebarProps = {
  collectionLinks: CollectionLink[];
  hasMore: boolean;
  fetchMore: () => void;
};
export function CollectionSidebar({
  collectionLinks,
  hasMore,
  fetchMore,
}: SidebarProps): JSX.Element {
  const { showModal } = useModal();
  const containerRef = useRef<HTMLDivElement>(null);
  const lastRowRef = useRef<HTMLDivElement>(null);
  useInfiniteScroll({
    containerRef,
    lastRowRef,
    fetchMore,
    hasMore,
    rowCount: collectionLinks.length,
  });
  const { data } = useSettingsQuery();
  const [isCollapsed, setCollapsed] = useSidebarCollapsed();
  function onClickNewCollection() {
    if (data) {
      showModal(CollectionAddModal, {});
    }
  }

  const toggleLabel = isCollapsed ? 'Expand' : 'Collapse';

  return (
    <div
      className={cx(sidebarStyles.sidebar, {
        [sidebarStyles.sidebarCollapsed]: isCollapsed,
      })}
      ref={containerRef}
    >
      <Box className={sidebarStyles.sidebarContent}>
        <FlexBox
          justifyContent={isCollapsed ? 'center' : 'flex-end'}
          className={sidebarStyles.toggle}
        >
          <IconButton
            variant="tertiary"
            icon={isCollapsed ? 'angle-double-right' : 'angle-double-left'}
            aria-label={toggleLabel}
            aria-expanded={!isCollapsed}
            onClick={() => setCollapsed(!isCollapsed)}
          />
        </FlexBox>
        <FlexBox flexDirection="column" gap="8px" margin="0px 0px 10px 0px">
          <NavLinkItems isCollapsed={isCollapsed} />
        </FlexBox>
        <FlexBox flexDirection="column" gap="8px">
          <FlexBox
            justifyContent={isCollapsed ? 'center' : 'space-between'}
            alignItems="center"
          >
            <Text.Heading
              className={cx(
                layoutStyles.subHeading,
                sidebarStyles.collectionsHeading,
              )}
              variant={'heading-4'}
              color="primary"
              as="div"
            >
              Collections
            </Text.Heading>

            <Tooltip
              content="New Collection"
              side={isCollapsed ? 'right' : undefined}
            >
              <IconButton
                variant="secondary"
                icon="plus"
                aria-label="New Collection"
                onClick={onClickNewCollection}
              />
            </Tooltip>
          </FlexBox>
          {/* The rail hides the collections, as the global sidebar hides its sections. */}
          <div className={sidebarStyles.collections}>
            {collectionLinks.map((collectionLink: CollectionLink) => {
              return (
                <CollectionItem {...collectionLink} key={collectionLink.id} />
              );
            })}
          </div>
          <div ref={lastRowRef} />
        </FlexBox>
      </Box>
      <KnowledgeSettingsLink isCollapsed={isCollapsed} />
    </div>
  );
}

function KnowledgeSettingsLink({ isCollapsed }: { isCollapsed: boolean }) {
  return (
    <NavLink
      title={isCollapsed ? 'Settings' : undefined}
      className={({ isActive }) => {
        return cx(sidebarStyles.sidebarLink, {
          [sidebarStyles.sidebarLinkActive]: isActive,
        });
      }}
      to={routeGenerator.knowledgeSettingsPath()}
    >
      <FlexBox
        gap="300"
        justifyContent="flex-start"
        alignItems="center"
        padding="200 300"
        borderRadius="100"
        minWidth="0"
        className={sidebarStyles.settingsLink}
      >
        <Icon className={sidebarStyles.linkIcon} name="cog" />
        <Text.Paragraph variant="3" className={sidebarStyles.label}>
          Settings
        </Text.Paragraph>
      </FlexBox>
    </NavLink>
  );
}

function NavLinkItems({ isCollapsed }: { isCollapsed: boolean }) {
  const isArticlesPage = window.location.pathname === '/knowledge/articles';
  const hasMoreThan1PublicationStatus =
    window.location.search.split('publicationStatus').length - 1 > 1;
  const isNeverPublished =
    isArticlesPage &&
    window.location.search.includes('publicationStatus=NeverPublished') &&
    !hasMoreThan1PublicationStatus;
  const isPublishedWithDraft =
    isArticlesPage &&
    window.location.search.includes('publicationStatus=PublishedWithDraft') &&
    !hasMoreThan1PublicationStatus;
  const viewArticlesPath = `${routeGenerator.articles('')}`;
  const articleSuggestionsPath = routeGenerator.articleSuggestions();
  const showArticleSuggestions = useShowArticleSuggestions();

  return (
    <>
      <NavLinkItem
        to={viewArticlesPath}
        label="All articles"
        iconName="file-text"
        isActive={isArticlesPage && !(isNeverPublished || isPublishedWithDraft)}
        isCollapsed={isCollapsed}
      />
      <NavLinkItem
        to={`${viewArticlesPath}?publicationStatus=NeverPublished`}
        label="Pre-release"
        iconName="file-lock"
        isActive={isNeverPublished}
        isCollapsed={isCollapsed}
      />
      <NavLinkItem
        to={`${viewArticlesPath}?publicationStatus=PublishedWithDraft`}
        label="Work in progress"
        iconName="file-draft"
        isActive={isPublishedWithDraft}
        isCollapsed={isCollapsed}
      />
      {showArticleSuggestions && (
        <NavLinkItem
          to={articleSuggestionsPath}
          label="Article suggestions"
          iconName="ai"
          // Unlike the entries above, this is its own path rather than the
          // articles path with different query params.
          isActive={window.location.pathname === articleSuggestionsPath}
          isCollapsed={isCollapsed}
        />
      )}
    </>
  );
}

type IconNameProps = {
  iconName: IconNames;
};

type IconProps = IconNameProps;

type NavLinkItemProps = {
  to: string;
  label: string;
  isActive: boolean;
  /** On the collapsed rail the label is clipped and moves to the title. */
  isCollapsed?: boolean;
} & IconProps;
export function NavLinkItem(props: NavLinkItemProps): JSX.Element {
  const { to, label, iconName, isActive, isCollapsed = false } = props;
  return (
    <NavLink
      to={to}
      // The global sidebar's links use a title too. Unlike a Tooltip, it adds no
      // wrapper that would take the link out of the rail's layout.
      title={isCollapsed ? label : undefined}
      className={() =>
        cx(sidebarStyles.articlesLink, {
          [sidebarStyles.articlesLinkActive]: isActive,
        })
      }
    >
      <FlexBox alignItems="center" justifyContent="space-between" minWidth="0">
        <FlexBox flexDirection="row" alignItems="center" gap="8px" minWidth="0">
          <Icon className={sidebarStyles.linkIcon} name={iconName} />
          <Text.Paragraph variant="3" className={sidebarStyles.label}>
            {label}
          </Text.Paragraph>
        </FlexBox>
      </FlexBox>
    </NavLink>
  );
}

export function CollectionItem({
  id,
  title,
  internalKbEnabled,
  internalOnly,
  isSyncedWithElevio,
}: CollectionLink): JSX.Element {
  return (
    <NavLink
      to={`/knowledge/collection/${id}`}
      className={({ isActive }) =>
        cx(sidebarStyles.collectionLink, {
          [sidebarStyles.collectionLinkActive]: isActive,
        })
      }
    >
      <FlexBox
        className={sidebarStyles.collectionListItem}
        alignItems="center"
        justifyContent="space-between"
      >
        <FlexBox flexDirection="row" alignItems="center" gap="16px">
          {internalOnly ? (
            <Tooltip content="Internal only">
              <Icon
                className={sidebarStyles.linkIcon}
                name="lock"
                aria-label="Internal only"
              />
            </Tooltip>
          ) : (
            <Icon className={sidebarStyles.linkIcon} name="copy" />
          )}
          <Text.Paragraph variant="3">{title}</Text.Paragraph>
        </FlexBox>
        <FlexBox alignItems="center" gap="200">
          {isSyncedWithElevio && (
            <NodeLockIconWithTooltip label="Synced from Elevio" />
          )}
          {!internalKbEnabled && (
            <Tooltip content="Not visible in Internal KB">
              <Icon
                className={sidebarStyles.notVisibleIcon}
                name="eye-slash"
                size={16}
              />
            </Tooltip>
          )}
        </FlexBox>
      </FlexBox>
    </NavLink>
  );
}
